/* eslint-disable quotes */

/**
 * Formats a supplement object and its related data to a payload suitable for sending to the backend.
 *
 * @param {Object} [supplement={}] - The supplement object to format.
 * @param {Object} [supplement.langs] - Object containing language-specific supplement data.
 * @param {Array<Object>} [supplement.effects] - Array of side effect objects, each with language data.
 * @param {Array<Object>} [supplement.deficiencies] - Array of deficiency objects, each with language data.
 * @param {Array<Object>} [supplement.excesses] - Array of excess objects, each with language data.
 * @param {Array<Object>} [supplement.activities] - Array of activity objects, each with language data.
 * @param {Array<Object>} [supplement.doses] - Array of dose objects.
 * @param {Array} [supplement.usedFor] - Array of purposes for which the supplement is used.
 * @param {Array} [supplement.benefits] - Array of supplement benefits.
 * @param {Array} [supplement.scienceSources] - Array of science source keys.
 * @param {string|number} [supplement.status] - Status of the supplement.
 * @param {string|number} [supplement.id] - ID of the supplement (used to determine edit mode).
 * @param {Array} [categories=[]] - Array of supplement category IDs.
 * @returns {[Object, boolean]} Returns an array where the first element is the formatted payload object and the second is a boolean indicating edit mode.
 */
export function formatToSend(supplement = {}, categories = []) {
  const editMode = !!supplement.id;

  const payload = {};
  const entriesLangs = Object.entries(supplement.langs) || [];
  const langKeys = Object.keys(supplement.langs || {});

  // Format languages supplement
  payload.langs = entriesLangs.map(([lang, data]) => ({
    lang: lang, // Supplement
    name: data.name, // Supplement
    familySupplement: data.familySupplement, // Supplement
    chemicalName: data.chemicalName, // Supplement
    description: data.description, // Supplement
    comments: data.comments, // Supplement
  }));

  // Maps a block of langs from an item to [{id, lang, value}, ...]
  const mapLangs = (itemLangs = {}) =>
    langKeys.map((langKey) => {
      const l = itemLangs[langKey] || {};
      return {
        id: l.id ?? null,
        lang: langKey,
        value: l.text ?? l.value ?? "",
      };
    });

  // Maps an item with langs to { id, langs: [...] }
  const mapItemWithLangs = (item = {}) => ({
    id: item.id ?? null,
    langs: mapLangs(item.langs),
  });

  // Format Side Effects and langs
  payload.sideEffects = (supplement.effects || []).map(mapItemWithLangs);

  // Deficiencies
  payload.deficiencies = (supplement.deficiencies || []).map(mapItemWithLangs);

  //  Excesses
  payload.excesses = (supplement.excesses || []).map(mapItemWithLangs);

  // Activities
  payload.activities = (supplement.activities || []).map(mapItemWithLangs);

  // Format categories
  payload.supplementCategories = categories;

  // Format purposes
  payload.purposes = (supplement.usedFor || []).filter(Boolean).map(Number);

  // Format supplements benefits
  // payload.supplementBenefits = supplement.benefits
  // NOTE: El backend reemplaza (upsert + soft-delete) las problemáticas de cada
  // beneficio con las `problemIds` enviadas. Si `supplementBenefits` viene vacío u
  // omitido, NO se eliminan los links existentes del beneficio.
  payload.supplementBenefits = supplement.benefits // TODO: Temporal el .filter hasta tener la instruccion de volver a poner las validaciones
    .filter(
      (b) => b.problemIds?.length > 0 || b.posology?.trim() || Object.values(b.score || {}).some((value) => value > 0)
    )
    .map((b) => ({
      id: b.id || null,
      posology: b.posology,
      problemIds: (b.problemIds || []).filter(Boolean).map(Number),
      score: {
        ec: b.score?.ec,
        bd: b.score?.bd,
        rc: b.score?.rc,
        est: b.score?.est,
      },
    }));
  // Format doses
  payload.doses = supplement.doses
    .filter((d) => {
      const allSpecificValuesValid = (d.specificValues || []).every(
        (v) => v !== null && v !== undefined && String(v).trim() !== ""
      );

      const allRangeValuesValid = (d.rangeValues || []).every((r) => r?.min != null && r?.max != null);

      return (
        d.ages &&
        d.biologicalSex?.trim() &&
        d.modality?.trim() &&
        d.unit?.trim() &&
        ((d.modality === "SPECIFIC" && allSpecificValuesValid) || (d.modality === "RANGE" && allRangeValuesValid))
      );
    })
    .map((d) => ({
      id: d.id || null,
      ageRanges: d.ages,
      biologicalSex: d.biologicalSex,
      modality: d.modality,
      rangeValues: d.rangeValues || [],
      specificValues: d.specificValues || [],
      unit: d.unit,
    }));

  // Science Source Keys
  payload.scienceSources = {
    east: (supplement.scienceSources?.east || [])
      .filter((s) => s.url && s.url.trim() !== "")
      .map((s) => ({
        id: s.id || null,
        platformId: s.platformId,
        url: s.url,
        platformReferenceId: s.platformReferenceId || "",
        doi: s.doi || "",
        description: s.description || "",
      })),
    west: (supplement.scienceSources?.west || [])
      .filter((s) => s.url && s.url.trim() !== "")
      .map((s) => ({
        id: s.id || null,
        platformId: s.platformId,
        url: s.url,
        platformReferenceId: s.platformReferenceId || "",
        doi: s.doi || "",
        description: s.description || "",
      })),
  };

  // Status
  payload.status = supplement.status;

  return [payload, editMode];
}
